<template>
    <div class="home-content">
        <!-- Header da página -->
        <PageHeader
            title="Dashboard de Reservas de Veículos"
            icon="fas fa-calendar-check"
            sub-title="Visualize e gerencie as reservas de veículos por horário"
        />

        <!-- Card principal -->
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="text-white font-weight-bolder">Reservas do dia</h5>
                <div class="header-actions">
                    <button
                        class="btn btn-outline-primary btn-action"
                        data-toggle="collapse"
                        data-target="#filterReservations"
                        aria-expanded="false"
                        aria-controls="filterReservations"
                    >
                        <i class="fas fa-filter"></i>
                        Filtros
                    </button>
                </div>
            </div>

            <!-- Filtros colapsáveis -->
            <div
                class="collapse"
                id="filterReservations"
            >
                <div class="filters-section">
                    <div class="row">
                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="branch">Filial</label>
                                <v-select
                                    id="branch"
                                    :options="branches"
                                    label="description"
                                    :reduce="(branch) => branch.id"
                                    v-model="filters.branch_id"
                                    placeholder="Todas as Filiais"
                                    :clearable="true"
                                    class="style-chooser"
                                >
                                    <div slot="no-options">Nenhuma filial encontrada</div>
                                </v-select>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="costCenter">Centro de Custo</label>
                                <v-select
                                    id="costCenter"
                                    :options="costCenters"
                                    label="erp_code"
                                    :reduce="(costCenter) => costCenter.id"
                                    v-model="filters.cost_center_id"
                                    placeholder="Todos os Centros de Custo"
                                    :clearable="true"
                                    class="style-chooser"
                                >
                                    <div slot="no-options">Nenhum centro de custo encontrado</div>
                                </v-select>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="form-group">
                                <label for="data">Data</label>
                                <input
                                    type="date"
                                    id="data"
                                    v-model="filters.date"
                                    class="form-control"
                                >
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="form-group">
                                <label>&nbsp;</label>
                                <div class="d-flex gap-2">
                                    <button
                                        class="btn btn-secondary"
                                        @click="resetFilters"
                                    >
                                        <i class="fas fa-eraser mr-1"></i>
                                        Limpar
                                    </button>

                                    <button
                                        class="btn btn-primary"
                                        @click="getFilters"
                                    >
                                        <i class="fas fa-search mr-1"></i>
                                        Filtrar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-body">

            <!-- Container principal com overflow adequado -->
            <div class="reservation-container">
                <div class="legend-items p-2">
                    <div class="legend-item">
                        <div class="legend-color pre-reserva"></div>
                        <span>Pré-reserva</span>
                    </div>
                    <div class="legend-item">
                        <div class="legend-color reservado"></div>
                        <span>Reservado</span>
                    </div>
                    <div class="legend-item">
                        <div class="legend-color retirado"></div>
                        <span>Retirado</span>
                    </div>
                    <div class="legend-item">
                        <div class="legend-color devolvido"></div>
                        <span>Devolvido</span>
                    </div>
                </div>

                <!-- Loading state -->
                <div
                    v-if="isLoading"
                    class="loading-state"
                >
                    <div class="loading-spinner">
                        <i class="fas fa-circle-notch fa-spin"></i>
                    </div>
                    <div class="loading-text">Carregando veículos...</div>
                </div>

                <!-- Empty state -->
                <div
                    v-else-if="vehicles.length === 0"
                    class="empty-state"
                >
                    <div class="empty-icon">
                        <i class="fas fa-car"></i>
                    </div>
                    <div class="empty-title">Nenhum veículo encontrado</div>
                    <div class="empty-subtitle">
                        Nenhum veículo disponível nas condições selecionadas.
                    </div>
                </div>

                <!-- Header fixo da tabela -->
                <div
                    v-else
                    class="table-header-wrapper"
                    ref="headerWrapper"
                    @scroll="syncScrollFromHeader"
                >
                    <table class="reservation-table-header">
                        <thead>
                        <tr>
                            <th class="vehicle-column">Veículo/Hora</th>
                            <th
                                v-for="hour in hoursToShow"
                                :key="hour"
                                class="hour-column"
                            >
                                {{ hour }}:00
                            </th>
                        </tr>
                        </thead>
                    </table>
                </div>

                <!-- Body com scroll da tabela -->
                <div
                    v-if="!isLoading && vehicles.length > 0"
                    class="table-body-wrapper"
                    ref="bodyWrapper"
                    @scroll="syncScrollFromBody"
                >
                    <table class="reservation-table-body">
                        <tbody>
                        <tr
                            v-for="(vehicle, index) in vehicles"
                            :key="vehicle.id"
                        >
                            <td class="vehicle-column">
                                <!-- Aplicação da lógica de ocultação / Application of hiding logic -->
                                <span v-if="shouldHideVehicleInfo(vehicle)">
                                    <span
                                        style="font-weight: 600"
                                    >
                                        Veículo {{ index + 1 }} - ***-***
                                    </span>
                                </span>
                                <span
                                    v-else
                                    style="font-weight: 600"
                                >
                                    {{ vehicle.description }} - {{ vehicle.board }}
                                </span>
                            </td>
                            <td
                                v-for="hour in hoursToShow"
                                :key="hour"
                                class="reservation-cell"
                                @click="!getReserve(hour, vehicle.id) ? createReservation(hour, vehicle) : null"
                                :class="{ 'clickable-cell': !getReserve(hour, vehicle.id) }"
                            >
                                <div
                                    v-if="getReserve(hour, vehicle.id)"
                                    class="reservation"
                                    :class="getReserveClass(hour, vehicle.id)"
                                    @click="getReserveDetails(getReserve(hour, vehicle.id), vehicle)"
                                >
                                    <strong class="text-truncate">{{
                                            getFirstName(getReserve(hour, vehicle.id).user)
                                                                  }}</strong><br>
                                    {{ getReserve(hour, vehicle.id).department }}
                                </div>
                            </td>
                        </tr>

                        </tbody>
                    </table>
                </div>
            </div>
            </div> <!-- fim card-body -->
        </div> <!-- fim card -->

        <!-- Modais -->
        <checkin-modal
            :modalStatus="modalCheckinStatus"
            :dataModal="dataModalCheckin"
            @changeModal="changeCheckinModal"
            @OpenModalUser="openModalUser"
            v-if="loadModalCheckin"
        ></checkin-modal>

        <user-modal
            :modalStatus="modalUserStatus"
            :dataModal="dataModalUser"
            title="Visualizar Usuário"
            @changeModal="changeUserModal"
        ></user-modal>
    </div>
</template>

<script>
import {fetchGet} from "../../composables/api";
import {addZeros} from "../../composables/functions";
import {shouldHideVehicleData} from "../../composables/useVehicleVisibility";
import BaseModal from '../Base/BaseModal.vue';
import SectionContainer from "../Modals/Checklist/SectionContainer.vue";
import CheckinModal from "../Modals/Checklist/ChecklistModal.vue";
import UserModal from "../Modals/UserModal.vue";
import PageHeader from "../Base/PageHeader.vue";
import vSelect from "vue-select";

export default {
    name: 'DashboardReservasVeiculos', // Nome do componente: Dashboard de Reservas de Veículos
    components: {
        UserModal,
        CheckinModal,
        BaseModal,
        SectionContainer,
        PageHeader,
        vSelect
    },
    data() {
        return {
            branches: [],
            costCenters: [],
            vehicles: [],
            reserveVehicles: [],
            modalVisualizarReserva: false,
            reservaSelecionada: null,
            hours: Array.from({length: 24}, (_, i) => i),
            filters: {
                branch_id: '',
                cost_center_id: '',
                date: this.getNow()
            },
            vehiclesFiltrados: [],
            reserveVehiclesFiltradas: [],
            modalCheckinStatus: false,
            dataModalCheckin: {},
            loadModalCheckin: false,
            modalUserStatus: false,
            dataModalUser: {},
            isScrolling: false,
            isLoading: false,
        };
    },

    computed: {
        hoursToShow() { // hoursToShow - horas a serem exibidas na tabela
            return this.hours.filter(hour => hour >= 0 && hour <= 23);
        }
    },

    methods: {
        /**
         * Verifica se deve ocultar informações do veículo
         * Check if vehicle information should be hidden
         * @param {Object} vehicle - Dados do veículo / Vehicle data
         * @returns {boolean} - True se deve ocultar / True if should hide
         */
        shouldHideVehicleInfo(vehicle) {
            try {
                const user = this.$store.state.user;

                if (!user) {
                    return true;
                }

                const vehicleReservations = this.reserveVehicles.filter(r => r.vehicleId === vehicle.id);

                const firstReservation = vehicleReservations[0];

                const reservationData = {
                    vehicles: {
                        id: vehicle.id,
                        description: vehicle.description,
                        board: vehicle.board,
                        cost_center_id: vehicle.cost_center_id,
                        costCenter: vehicle.costCenter
                    },
                    retreat_date: this.buildFullRetreatDate(firstReservation),
                    vehicle_id: vehicle.id,
                    user_id: firstReservation ? firstReservation.user_id : null,
                };

                return shouldHideVehicleData(user, reservationData);

            } catch (error) {
                console.error('Erro ao verificar visibilidade do veículo no dashboard:', error);
                return false;
            }
        },

        /**
         * Constrói a data completa de retirada para comparação
         * Build complete retreat date for comparison
         * @param {string} retreatHour - Hora de retirada / Retreat hour
         * @returns {string} - Data completa / Complete date
         */
        buildFullRetreatDate(retreatHour) {
            if (!retreatHour || !this.filters.date) {
                return null;
            }

            // Converte "14" para "2024-01-15 14:00:00" / Convert "14" to "2024-01-15 14:00:00"
            const hour = String(retreatHour.retreat_date).padStart(2, '0');
            return `${this.filters.date} ${hour}:00:00`;
        },

        changeUserModal(status) {
            this.modalUserStatus = status;
        },
        changeCheckinModal(status) {
            this.modalCheckinStatus = status;
        },
        openModalUser(object) {
            this.modalUserStatus = true;
            this.dataModalUser = object;
            this.loadModalUser = true;
        },
        getNow() {
            const hoje = new Date();
            return hoje.toISOString().split('T')[0];
        },

        resetFilters() {
            let date = new Date();
            date = `${date.getFullYear()}-${addZeros(date.getMonth() + 1)}-${addZeros(date.getDate())}`;
            this.filters = {
                branch_id: '',
                cost_center_id: '',
                date
            };
            this.getFilters();
        },

        async getFilters() {
            try {
                this.isLoading = true;

                let params = {
                    branch_id: this.filters.branch_id,
                    cost_center_id: this.filters.cost_center_id,
                }

                const [reserveVehiclesData, vehiclesData] = await Promise.all([
                    fetchGet('dashboard/reserveVehicles', this.filters),
                    fetchGet('vehicles/vehicles-for-home', params),
                ]);

                this.reserveVehicles = reserveVehiclesData.data.data;
                this.vehicles = vehiclesData.data.data;
            } catch (error) {
                console.error('Erro ao buscar reservas:', error);
            } finally {
                this.isLoading = false;
            }
        },

        getReserve(hour, vehicleId) {
            return this.reserveVehicles.find(r =>
                r.vehicleId === vehicleId &&
                hour >= r.retreat_date &&
                hour <= r.devolution_date
            );
        },

        getReserveClass(hour, vehicleId) {
            const reserva = this.getReserve(hour, vehicleId);
            if (!reserva) return '';

            const statusClasses = {
                1: 'pre-reserva',
                2: 'reservado',
                3: 'cancelado',
                4: 'retirado',
                5: 'devolvido'
            };

            return statusClasses[reserva.status] || '';
        },

        getFirstName(name) {
            if (!name || typeof name !== 'string') return '';
            const arr = name.split(' ');
            return arr[0];
        },

        getStatusClass(status) {
            const statusClasses = {
                1: 'status-pre-reserva',
                2: 'status-reservado',
                3: 'status-cancelado',
                4: 'status-retirado',
                5: 'status-devolvido'
            };
            return statusClasses[status] || 'status-default';
        },

        fecharModal() {
            this.modalVisualizarReserva = false;
            this.reservaSelecionada = null;
        },

        async getReserveDetails(reserve) {
            try {
                const params = {
                    with: [
                        'user',
                        'checkout',
                        'checkin',
                        'vehicle_type_id',
                        'vehicles',
                        'reserve_status',
                        'cityDestination',
                    ]
                }
                const reserveData = await fetchGet('reserveVehicles/' + reserve.id, params);
                this.dataModalCheckin = {...reserveData.data};
                this.modalCheckinStatus = true;
                this.loadModalCheckin = true;
            } catch (error) {
                console.error('Erro ao carregar detalhes da reserva:', error);
            }
        },

        async getDatum() { // getDatum - carrega todos os dados necessários para o dashboard
            try {
                this.isLoading = true;

                let params = {
                    paginated: false,
                    vehicle_status_id: 1,
                    branch_id: this.filters.branch_id,
                    with: ['costCenter']
                }

                if (this.filters.cost_center_id && this.filters.cost_center_id !== '') params.cost_center_id = this.filters.cost_center_id;

                const [reserveVehiclesData, branchesData, vehiclesData, costCentersData] = await Promise.all([
                    fetchGet('dashboard/reserveVehicles', this.filters),
                    fetchGet('branches', {
                        paginated: false
                    }),
                    fetchGet('vehicles', params),
                    fetchGet('cost-centers', {
                        paginated: false
                    }),
                ]);

                this.reserveVehicles = reserveVehiclesData.data.data;
                this.branches = branchesData.data.data;
                this.vehicles = vehiclesData.data.data;

                // Adiciona opção especial para veículos sem centro de custo
                this.costCenters = [
                    {id: '-1', erp_code: 'Veículos de locação'},
                    ...costCentersData.data.data
                ];
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            } finally {
                this.isLoading = false;
            }
        },

        createReservation(hour, vehicle) {
            // Cria a data de retirada baseada no horário selecionado
            const retreatDate = `${this.filters.date} ${hour.toString().padStart(2, '0')}:00:00`;

            // Cria a data de devolução padrão (1 hora após a retirada)
            const devolutionDate = `${this.filters.date} ${(hour + 1).toString().padStart(2, '0')}:00:00`;

            // Prepara os parâmetros para envio
            const queryParams = {
                vehicle_id: vehicle.id,
                retreat_date: retreatDate,
                devolution_date: devolutionDate,
                branch_id: vehicle.branch_id || this.filters.branch_id
            };

            // Adiciona centro de custos se disponível
            if (vehicle.cost_center_id) {
                queryParams.cost_center_id = vehicle.cost_center_id;
            }

            // Adiciona tipo do veículo se disponível
            if (vehicle.vehicle_type_id) {
                queryParams.vehicle_type_id = vehicle.vehicle_type_id;
            }

            // Redireciona para a página de cadastro com os parâmetros
            this.$router.push({
                path: '/myreserves/add',
                query: queryParams
            });
        },

        syncScrollFromBody() {
            // Sincroniza o scroll horizontal do body para o header
            if (this.isScrolling) return;

            this.isScrolling = true;
            if (this.$refs.headerWrapper && this.$refs.bodyWrapper) {
                this.$refs.headerWrapper.scrollLeft = this.$refs.bodyWrapper.scrollLeft;
            }

            this.$nextTick(() => {
                this.isScrolling = false;
            });
        },

        syncScrollFromHeader() {
            // Sincroniza o scroll horizontal do header para o body
            if (this.isScrolling) return;

            this.isScrolling = true;
            if (this.$refs.headerWrapper && this.$refs.bodyWrapper) {
                this.$refs.bodyWrapper.scrollLeft = this.$refs.headerWrapper.scrollLeft;
            }

            this.$nextTick(() => {
                this.isScrolling = false;
            });
        }
    },

    created() {
        this.getFilters();
    },

    mounted() {
        // Configura a data atual nos filtros
        let date = new Date();
        date = `${date.getFullYear()}-${addZeros(date.getMonth() + 1)}-${addZeros(date.getDate())}`;
        this.filters.date = date;
        this.getDatum();
    }
}
</script>

<style scoped>
@import "../../assets/css/home.css";

/* Container principal das tabelas */
.reservation-container {
    display: flex;
    flex-direction: column;
    max-height: 80vh;
}

/* Wrapper do header fixo */
.table-header-wrapper {
    flex-shrink: 0;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #dee2e6;
    border-bottom: none;
    background: #343a40;
}

/* Tabela do header */
.reservation-table-header {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin: 0;
}

.reservation-table-header thead th {
    background: #343a40;
    color: white;
    padding: 12px 8px;
    font-weight: 600;
    text-align: center;
    border-right: 1px solid #495057;
    white-space: nowrap;
    vertical-align: middle;
}

/* Primeira coluna do header - fica fixa à esquerda */
.reservation-table-header .vehicle-column {
    position: sticky;
    left: 0;
    z-index: 101;
    background: #343a40;
    border-right: 2px solid #6c757d;
    width: 200px;
    min-width: 200px;
    max-width: 200px;
}

/* Colunas de hora do header */
.reservation-table-header .hour-column {
    width: 80px;
    min-width: 80px;
}

/* Wrapper do body com scroll */
.table-body-wrapper {
    flex: 1;
    overflow: auto;
    border: 1px solid #dee2e6;
    border-top: none;
}

/* Tabela do body */
.reservation-table-body {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin: 0;
}

/* Primeira coluna do body - fica fixa à esquerda */
.reservation-table-body .vehicle-column {
    position: sticky;
    left: 0;
    z-index: 100;
    border-right: 2px solid #dee2e6;
    width: 200px;
    min-width: 200px;
    max-width: 200px;
    padding: 8px;
    vertical-align: middle;
    font-weight: 500;
    background: #343a40;
}

/* Blocos de reserva / Reservation blocks */
.reservation {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    padding: 4px 2px;
    font-size: 11px;
    line-height: 1.2;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    word-wrap: break-word;
    overflow: hidden;
}

.reservation:hover {
    transform: scale(1.02);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    z-index: 10;
}


.legend-color {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.2);
}

/* Scrollbar customizada / Custom scrollbar */
.table-wrapper::-webkit-scrollbar {
    height: 12px;
    width: 12px;
}

.table-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
}

.table-wrapper::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 6px;
    transition: background 0.2s ease;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.table-wrapper::-webkit-scrollbar-corner {
    background: #f1f1f1;
}

/* Estilos para células clicáveis / Styles for clickable cells */
.clickable-cell {
    cursor: pointer;
    position: relative;
    transition: background-color 0.2s ease;
}

.clickable-cell:hover {
    background-color: #f8f9fa;
}

.clickable-cell:hover::after {
    content: '+';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
    color: #28a745;
    font-weight: bold;
}

.reservation-cell {
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    height: 60px;
    padding: 2px;
    text-align: center;
    vertical-align: middle;
    border: 1px solid #dee2e6;
    position: relative;
    background: #fff;
}

/* Loading state styles */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    margin: 20px 0;
}

.loading-spinner {
    font-size: 48px;
    color: #f8981d;
    margin-bottom: 20px;
}

.loading-spinner i {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.loading-text {
    font-size: 18px;
    color: #6c757d;
    font-weight: 500;
}

/* Estados de loading e vazio usando classes existentes */
.loading-state, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.loading-spinner, .empty-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
    font-size: 2rem;
    color: #6c757d;
}

.loading-spinner i {
    color: #f8981d;
}

.loading-text, .empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
}

.empty-subtitle {
    color: #6c757d;
    font-size: 1rem;
}

/* Layout principal */
.home-content {
    padding: 20px;
}

/* Seção de filtros */
.filters-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-top: 1px solid #dee2e6;
}

.filters-section .form-group {
    margin-bottom: 1rem;
}

.filters-section label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
}

/* Botão de ação do header */
.btn-action {
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    transition: all 0.2s ease;
}

.btn-action:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-action i {
    margin-right: 8px;
}

/* Card principal */
.card {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
    background-color: #fff;
    border-bottom: 1px solid #dee2e6;
    padding: 1rem 1.25rem;
}

.card-body {
    padding: 0; /* Remove padding para dar espaço total à tabela */
}

/* Ajustes para os botões de filtro */
.d-flex.gap-2 {
    gap: 0.5rem;
}

.d-flex.gap-2 .btn {
    flex: 1;
}

/* Container da tabela de reservas */
.reservation-container {
    margin: 0;
    border-radius: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .home-content {
        padding: 10px;
    }

    .filters-section {
        padding: 15px;
    }

    .filters-section .row .col-md-3 {
        margin-bottom: 1rem;
    }

    .d-flex.gap-2 {
        flex-direction: column;
    }

    .d-flex.gap-2 .btn {
        width: 100%;
        margin-bottom: 0.5rem;
    }

    .loading-state,
    .empty-state {
        padding: 40px 20px;
    }

    .loading-spinner {
        font-size: 36px;
    }

    .loading-text {
        font-size: 16px;
    }

    .empty-icon {
        font-size: 48px;
    }

    .empty-title {
        font-size: 20px;
    }

    .empty-subtitle {
        font-size: 14px;
    }
}

/* Customização do v-select */
.style-chooser >>> .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 2px 6px;
    min-height: 38px;
}

.style-chooser >>> .vs__dropdown-toggle:hover {
    border-color: #80bdff;
}

.style-chooser >>> .vs__selected {
    color: #495057;
}

.style-chooser >>> .vs__search::placeholder {
    color: #6c757d;
}

.style-chooser >>> .vs__clear {
    margin-right: 8px;
}

</style>



// WEBPACK FOOTER //
// src/components/Home/Home.vue